import { Box, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type {
  ResolvedStatus,
  StatusHistoryEntry,
  StatusResolver
} from '@/features/history/statusHistoryTypes';
import { ListPagination, StatusPill, tableRowStyle } from '@/components/common';
import { formatDateTime } from '@/utils/format';
import { useLocalPagination } from '@/utils/useLocalPagination';

interface StatusHistoryTableProps {
  entries: StatusHistoryEntry[];
  resolveStatus: StatusResolver;
}

const RoleBadge = ({ role }: { role: 'PARTNER' | 'CLIENT' | null }) => {
  const { t } = useTranslation();
  if (!role) {
    return null;
  }
  return (
    <Text
      fz={10.5}
      fw={600}
      tt="uppercase"
      px={8}
      py={2}
      style={{
        borderRadius: 999,
        width: 'fit-content',
        backgroundColor: 'var(--color-bg-surface-2)',
        color: 'var(--color-text-secondary)',
        letterSpacing: 0.3
      }}
    >
      {t(role === 'PARTNER' ? 'common.rolePartner' : 'common.roleClient')}
    </Text>
  );
};

const StatusTransition = ({
  previous,
  current
}: {
  previous: ResolvedStatus | null;
  current: ResolvedStatus;
}) => (
  <Group gap={8} wrap="nowrap">
    {previous && (
      <>
        <StatusPill label={previous.label} bg={previous.bg} fg={previous.fg} />
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          →
        </Text>
      </>
    )}
    <StatusPill label={current.label} bg={current.bg} fg={current.fg} />
  </Group>
);

/** AUD-01/AUD-02 — "Povijest statusa" tablica/kartice, dijeljeno za svih 6 entiteta. Klijentska
 * (ne server-side) paginacija — povijest jednog entiteta je po prirodi mala lista, isti pristup
 * kao ostale in-page pod-liste (namjerno izvan GEN-02/03 opsega). */
export const StatusHistoryTable = ({ entries, resolveStatus }: StatusHistoryTableProps) => {
  const { t } = useTranslation();
  const { page, setPage, pageSize, setPageSize, totalPages, pageItems } =
    useLocalPagination(entries);
  const pageEntries = pageItems;

  const columns: [string, number | undefined, 'left' | 'right'][] = [
    [t('statusHistory.colDateTime'), 170, 'left'],
    [t('statusHistory.colStatusChange'), 320, 'left'],
    [t('statusHistory.colChangedBy'), 220, 'left'],
    [t('statusHistory.colOrganisation'), undefined, 'left']
  ];

  return (
    <Stack gap={16}>
      <Group justify="space-between" align="baseline">
        <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('statusHistory.title')}
        </Text>
        <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
          {t('statusHistory.count', { count: entries.length })}
        </Text>
      </Group>

      <Box
        visibleFrom="sm"
        style={{
          border: '1px solid var(--color-border-soft)',
          borderRadius: 12,
          overflow: 'hidden'
        }}
      >
        <Box style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 900 }}>
            <div
              className="table-header-row"
              style={{
                ...tableRowStyle,
                padding: '12px 20px',
                backgroundColor: 'var(--color-bg-canvas)',
                borderBottom: '1px solid var(--color-border-soft)'
              }}
            >
              {columns.map(([label, width]) => (
                <Text
                  key={label}
                  fz={10}
                  fw={600}
                  tt="uppercase"
                  style={{
                    width,
                    flex: width ? undefined : 1,
                    color: 'var(--color-text-secondary)',
                    letterSpacing: 0.3
                  }}
                >
                  {label}
                </Text>
              ))}
            </div>
            {pageEntries.length === 0 ? (
              <Text fz={13} p={20} style={{ color: 'var(--color-text-muted)' }}>
                {t('statusHistory.empty')}
              </Text>
            ) : (
              pageEntries.map((entry, index) => {
                const current = resolveStatus(entry.newStatus);
                const previous = entry.previousStatus ? resolveStatus(entry.previousStatus) : null;
                return (
                  <div
                    key={`${entry.occurredAt}-${index}`}
                    style={{
                      ...tableRowStyle,
                      padding: '14px 20px',
                      borderBottom: '1px solid var(--color-border-soft)'
                    }}
                  >
                    <Text fz={13} style={{ width: 170, color: 'var(--color-text-primary)' }}>
                      {formatDateTime(entry.occurredAt)}
                    </Text>
                    <Box style={{ width: 320 }}>
                      <StatusTransition previous={previous} current={current} />
                    </Box>
                    <Text
                      fz={13}
                      fw={600}
                      style={{ width: 220, color: 'var(--color-text-primary)' }}
                    >
                      {entry.changedByName ?? t('statusHistory.systemActor')}
                    </Text>
                    <Group gap={8} style={{ flex: 1 }} wrap="nowrap">
                      <Text fz={13} style={{ color: 'var(--color-text-primary)' }}>
                        {entry.organisationName ?? '—'}
                      </Text>
                      <RoleBadge role={entry.organisationRole} />
                    </Group>
                  </div>
                );
              })
            )}
          </div>
        </Box>
      </Box>

      <Stack gap={10} hiddenFrom="sm">
        {pageEntries.length === 0 ? (
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('statusHistory.empty')}
          </Text>
        ) : (
          pageEntries.map((entry, index) => {
            const current = resolveStatus(entry.newStatus);
            const previous = entry.previousStatus ? resolveStatus(entry.previousStatus) : null;
            return (
              <Box
                key={`${entry.occurredAt}-${index}`}
                p={16}
                style={{
                  borderRadius: 12,
                  border: '1px solid var(--color-border-soft)',
                  backgroundColor: 'var(--color-bg-surface)'
                }}
              >
                <Stack gap={8}>
                  <StatusTransition previous={previous} current={current} />
                  <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {entry.changedByName ?? t('statusHistory.systemActor')}
                  </Text>
                  <Group gap={8} wrap="nowrap">
                    <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                      {entry.organisationName ?? '—'}
                    </Text>
                    <RoleBadge role={entry.organisationRole} />
                  </Group>
                  <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                    {formatDateTime(entry.occurredAt)}
                  </Text>
                </Stack>
              </Box>
            );
          })
        )}
      </Stack>

      {entries.length > 0 && (
        <ListPagination
          label={t('statusHistory.showing', {
            shown: pageEntries.length,
            total: entries.length
          })}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
      )}
    </Stack>
  );
};
